<template>
    <span>
        <OmniButton v-if="alwaysShow || showPackingSlipButton"
                  @click="downloadPackingSlip" :variant="variant" v-bind="[$props, $attrs]"
                  :disabled="isPackingSlipRequested"
        >
            <template #icon>
                <font-awesome-layers v-if="!isPackingSlipRequested" v-bind="[$attrs]">
                    <font-awesome-icon icon="file-pdf"/>
                    <font-awesome-icon v-if="askLabelCount || ignoreCache" icon="circle-plus" transform="shrink-5" class="text-info ml-2 mb-2"/>
                </font-awesome-layers>

                <font-awesome-icon v-else icon="spinner" spin v-bind="[$attrs]"/>
            </template>

            <slot>
                <span v-if="!isPackingSlipRequested" v-bind="[$attrs]">
                    <template v-if="askLabelCount">
                        <span v-if="requestedLabelCount !== null">
                            Download pakbon en {{ requestedLabelCount }} vervoerslabel{{ requestedLabelCount > 1 ? 's' : '' }}
                        </span>
                    </template>

                    <span v-else v-bind="[$attrs]">
                        Download pakbon en vervoerslabel
                    </span>
                </span>

                <span v-else v-bind="[$attrs]">
                    Download pakbon en vervoerslabel
                </span>
            </slot>
        </OmniButton>

        <b-modal v-model="labelCountModalVisible" size="md" centered dialog-class="shadow-none"
                 title="Kies aantal labels"
                 @ok="downloadPackingSlip"
        >
            <b-row>
                <b-col cols="10">Hoeveel labels wil je downloaden?</b-col>
                <b-col cols="2"><b-input type="number" size="sm" :min="minimumLabelCount" :max="maximumLabelCount" v-model="requestLabelCount" autofocus/></b-col>
            </b-row>
            <b-row v-if="isAutoPrintLabelsEnabled" class="mt-2">
                <b-col cols="10">Print labels</b-col>
                <b-col cols="2"><b-checkbox v-model="printLabels" switch/></b-col>
            </b-row>
            <b-row v-if="isAutoPrintPackingSlipEnabled" class="mt-2">
                <b-col cols="10">Print pakbon</b-col>
                <b-col cols="2"><b-checkbox v-model="printLabels" switch/></b-col>
            </b-row>
        </b-modal>
    </span>
</template>

<script>
import {downloadData, downloadBase64url, getDataURL} from "../../../helpers/download";
import _ from "lodash"

const ALLOW_STATUSES = [
    'ready-to-ship',
    'requested',
    'shipped',  // Willen we een nieuw label kunnen aanmaken voor het geval dat de carrier het product niet aflevert en direct terugstuurt naar de afzender.
]

const SHOW_FOR_DISPATCH_TYPES = [
    'click_and_collect',
    'ship_from_store',
]

import OmniButton from '@/components/OmniStock/OmniButton.vue';
import {dispatchRequestStatus} from "../../../helpers/dispatch";
import {downloadPackingSlipButtonBase} from "@/views/OrderDetail/components/downloadPackingSlipButtonBase";

export default {
    name: "DownloadPackingSlipButton",
    extends: downloadPackingSlipButtonBase,

    components: {
        OmniButton,
    },

    props: {
        dispatchRequest: {required: true},
        variant: {default: 'link'},
        askLabelCount: {default: false},
        alwaysShow: {default: false},
        ignoreCache: {default: false},
    },

    data() {
        return {
            isPackingSlipRequested: false,
            requestLabelCount: 0,
            labelCountModalVisible: false,
            requestedLabelCount: null, // Used to store the requested amount so we can set this as the minimum without reloading the dispatch_request.tracking_codes
            printLabels: false,
            printPackingSlip: false,
        }
    },

    computed: {
        title() {
            return (this.ignoreCache)
                ? "Download een nieuwe pakbon en vervoerslabel"
                : "Download pakbon en vervoerslabel"
        },
        icon() {
            return (this.askLabelCount || this.ignoreCache)
                ? 'file-pdf fa-stack circle-plus'
                : 'file-pdf'
        },
        uniqueTrackingCodes() {
            return _.uniq((this.dispatchRequest.tracking_codes ?? []).map(t => t.code)).length
        },
        showPackingSlipButton() {
            return this.hasPermission('orders.dispatchrequests.downloadpackingslip')
                && SHOW_FOR_DISPATCH_TYPES.includes(this.dispatchRequest.dispatch_type?.identifier)
                && (
                    !this.askLabelCount
                    || this.minimumLabelCount !== this.maximumLabelCount
                )
        },
        minimumLabelCount() {
            if (this.requestLabelCount) {
                return this.requestLabelCount
            }

            let count = this.uniqueTrackingCodes
            if (!count) count = 1
            return count
        },
        maximumLabelCount() {
            return this.dispatchRequest?.dispatch_request_lines.reduce((sum, line) => sum + line.quantity, 0) ?? 999
        }
    },

    mounted() {
        this.requestLabelCount = this.minimumLabelCount
    },

    methods: {
        downloadPackingSlip() {
            if (this.askLabelCount && this.labelCountModalVisible === false) {
                this.labelCountModalVisible = true;
                return
            }

            if (this.isPackingSlipRequested) {
                return
            }

            if (this.dispatchRequest.carrier_method && !this.dispatchRequest.carrier_method.has_label_config) {
                this.$alert('De orderbon kan niet gedownload worden omdat deze vervoerder/bezorgwijze daar niet voor is geconfigureerd.', 'danger')
                return
            }

            if (ALLOW_STATUSES.indexOf(this.dispatchRequest.status) === -1) {
                this.$alert(`De orderbon kan niet gedownload worden voor een levering met de status '${dispatchRequestStatus(this.dispatchRequest.status)}'`, 'danger')
                return
            }

            this.isPackingSlipRequested = true
            let extension

            this.labelCountModalVisible = false;

            this.$http.get(
                `/api/rest/dispatch-requests/id:${this.dispatchRequest.id}/packing-slip`,
                {
                    params: {
                        separate_label: this.$store.state.config['packing-slip-separate-label'] ? 1 : 0,
                        auto_print_label: this.printLabels ? 1 : 0,
                        auto_print_packing_slip: this.printPackingSlip ? 1 : 0,
                        label_count: this.requestLabelCount,
                        ignore_cache: this.ignoreCache ? 1 : 0,
                    }
                }
            )
                .then(response => {
                    this.requestedLabelCount = this.requestLabelCount

                    extension = response.data.packing_slip_content_type?.split('/')[1]
                    downloadBase64url(getDataURL(response.data.packing_slip_content_type, response.data.packing_slip_content_base64), `Packingslip-${this.dispatchRequest.identifier}.${extension}`)

                    if (this.printPackingSlip) {
                        this.$alert(`Er is een automatische printopdracht verstuurd voor de pakbon`, 'info')
                    }

                    let i = 1;
                    if (response.data['labels-content-type']) {
                        extension = response.data['labels-content-type'].split('/')[1]
                        _.each(response.data.labels ?? {}, (label, barcode) => {
                            downloadBase64url(getDataURL(response.data['labels-content-type'], label), `Label-${this.dispatchRequest.identifier}-${i}.${extension}`)

                            if (this.printLabels) {
                                this.$alert(`Er is een automatische printopdracht verstuurd voor het label met barcode: ${barcode}`, 'info')
                            }

                            i++
                        })
                    } else {
                        this.$alert(`Er zijn geen labels om te printen`, 'info')
                    }
                })
                .catch(e => {
                    console.error(e)

                    switch (e.response.data?.errors?.[0]?.identifier) {
                        case '100':
                            this.$alert('De orderbon kan niet gedownload worden omdat deze vervoerder/bezorgwijze daar niet voor is geconfigureerd. Neem contact op met OmniStock.', 'danger', true)
                            break
                        case '101':
                            this.$alert('De orderbon kan niet gedownload worden omdat deze vervoersmethode niet is gekoppeld aan een management-service. Neem contact op met OmniStock.', 'danger', true)
                            break
                        default:
                            this.$alert('Onbekende fout bij het ophalen van de orderbon.', 'danger', true)
                            break
                    }
                })
                .then(() => {
                    this.isPackingSlipRequested = false
                })
        },
    },
}
</script>
